import { ApprovalData } from "./FieldMappings.types";
import { makeCygnusApiCall } from "../../../api/api";

/**
 * Fetches the approval page data from the API
 * @param userName - The user name
 * @param userRole - The user role
 * @returns A Promise resolving to the approval details
 */
export const fetchApprovalPageData = async (
  userName: string,
  userRole: string
): Promise<ApprovalData[]> => {

  const params = `?ApproverName=${encodeURIComponent(userName)}&ApproverRole=${encodeURIComponent(userRole)}&ApprovalStatus=Pending Approval&SendForApproval=Y`;

  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/engagement-letter/el_approvals/${params}`,
    show_message: true,
    customized_success_message: "Fetched approval data successfully.",
    customized_failure_message: "Failed to fetch approval data.",
    customized_error_message: "Error occurred while fetching approval data.",
    isblob: false,
    forMDM: true,
  });

  return response?.data || [];
};

/**
 * Saves the engagement letter approval details
 * @param selectedRows - Selected rows with approval data
 * @param approvalStatus - The approval status ("Approved" or "Rejected")
 * @param message - Optional message for rejection
 * @returns A Promise resolving to a success boolean
 */
export const saveEngagementLetterApprovalDetails = async (
  selectedRows: ApprovalData[],
  approvalStatus: "Approved" | "Rejected",
  message: string = ""
): Promise<boolean> => {
  if (selectedRows.length === 0) return false;

  const requestBody = selectedRows.map(row => ({
    ...row,
    ApprovalStatus: approvalStatus,
    Message: message,
  }));

  const response = await makeCygnusApiCall({
    type: "PUT",
    url: "/engagement-letter/el_approvals/",
    payload: requestBody,
    show_message: true,
    customized_success_message: "Approval details saved successfully.",
    customized_failure_message: "Failed to save approval details.",
    customized_error_message: "Error occurred while saving approval details.",
    isblob: false,
    forMDM: true,
  });

  return response?.success ?? false;
};

/**
 * Fetch firm configurations / reference data (domain or code).
 * @param {string} domain - ReferenceDataDomain (e.g. "ClientType").
 * @param {string} [code] - Optional ReferenceDataCode (e.g. "CLIENT_REJECT_OPTIONS").
 */
export const fetchFirmReferenceData = async (
  domain?: string,
  code?: string
): Promise<any> => {
  const query = new URLSearchParams();
  if (domain) query.append("ReferenceDataDomain", domain);
  if (code) query.append("ReferenceDataCode", code);

  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/firm_reference_data?${query.toString()}`,
    show_message: true,
    customized_success_message: "Fetched firm reference data.",
    customized_failure_message:
      "Unable to fetch firm reference data. Please try again or contact support.",
    customized_error_message:
      "Error occurred while fetching firm reference data.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) return response.data;
  return [];
};
